/**
 * Safely copy text to clipboard with fallback for older browsers
 * @param text - The text to copy to clipboard
 * @returns Promise<boolean> - Returns true if successful, false otherwise
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    // Modern clipboard API
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers or when clipboard API is not available
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      return successful;
    } finally {
      document.body.removeChild(textArea);
    }
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
}

/**
 * Copy text to clipboard and handle success/error states
 * @param text - The text to copy
 * @param onSuccess - Callback when copy succeeds
 * @param onError - Callback when copy fails
 */
export async function handleClipboardCopy(
  text: string,
  onSuccess?: () => void,
  onError?: (error: Error) => void,
): Promise<void> {
  try {
    const success = await copyToClipboard(text);
    if (success) {
      onSuccess?.();
    } else {
      throw new Error('Failed to copy to clipboard');
    }
  } catch (error) {
    const err = error instanceof Error ? error : new Error('Unknown clipboard error');
    onError?.(err);
  }
}
